import { User, UserPermissions } from 'src/types';

/** Defaults match API: all true, including canCreateDashboard. */
export const DEFAULT_USER_PERMISSIONS: UserPermissions = {
  positiveTargetIab: true,
  negativeTargetIab: true,
  positiveTargetGenre: true,
  negativeTargetGenre: true,
  editBrandStandardFilters: true,
  editBrandSuitabilityFilters: true,
  canCreateDashboard: true,
};

export type UserPermissionKey = keyof UserPermissions;

export const USER_PERMISSION_OPTIONS: {
  key: UserPermissionKey;
  label: string;
  /** When true, checkbox UI is inverted (checked = permission denied). */
  inverted?: boolean;
}[] = [
  { key: 'positiveTargetIab', label: 'Positive target IAB' },
  { key: 'negativeTargetIab', label: 'Negative target IAB' },
  { key: 'positiveTargetGenre', label: 'Positive target Genre' },
  { key: 'negativeTargetGenre', label: 'Negative target Genre' },
  { key: 'editBrandStandardFilters', label: 'Edit brand standard filters' },
  {
    key: 'editBrandSuitabilityFilters',
    label: 'Edit brand suitability filters',
  },
  {
    key: 'canCreateDashboard',
    label: 'Don\'t allow create new dashboard',
    inverted: true,
  },
];

type UserLike = Pick<User, 'role' | 'permissions'> | null | undefined;

export const getUserPermissions = (user?: UserLike): UserPermissions => ({
  ...DEFAULT_USER_PERMISSIONS,
  ...user?.permissions,
});

const canEdit = (user?: UserLike): boolean => !!user && user.role !== 'view';

const hasPermission = (
  user: UserLike | undefined,
  key: UserPermissionKey
): boolean => !!user && getUserPermissions(user)[key];

export const canCreateDashboard = (user?: UserLike): boolean =>
  canEdit(user) && getUserPermissions(user).canCreateDashboard;

export const canEditPositiveTargetIab = (user?: UserLike): boolean =>
  canEdit(user) && hasPermission(user, 'positiveTargetIab');

export const canEditNegativeTargetIab = (user?: UserLike): boolean =>
  canEdit(user) && hasPermission(user, 'negativeTargetIab');

export const canEditPositiveTargetGenre = (user?: UserLike): boolean =>
  canEdit(user) && hasPermission(user, 'positiveTargetGenre');

export const canEditNegativeTargetGenre = (user?: UserLike): boolean =>
  canEdit(user) && hasPermission(user, 'negativeTargetGenre');

export const canEditBrandStandardFilters = (user?: UserLike): boolean =>
  canEdit(user) && hasPermission(user, 'editBrandStandardFilters');

export const canEditBrandSuitabilityFilters = (user?: UserLike): boolean =>
  canEdit(user) && hasPermission(user, 'editBrandSuitabilityFilters');
